import type * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-base transition-all disabled:pointer-events-none \
  disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none \
  focus-visible:ring-ring/50 focus-visible:ring-[2px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 \
  aria-invalid:border-destructive [&_svg]:text-muted-foreground font-medium",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-foreground border border-primary\
          hover:bg-[var(--color-brand-300)]\
          focus-visible:border-[var(--color-brand-500)] focus-visible:ring-[var(--color-brand-200)]\
          active:border-[var(--color-brand-500)] active:bg-[var(--color-brand-500)]\
          disabled:border-primary/50 disabled:bg-primary [&_svg]:text-foreground",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 border",
        secondary:
          "bg-base text-muted-foreground border border-border\
          hover:border-border-bold hover:bg-faint\
          focus-visible:border-border-bold focus-visible:bg-subtle focus-visible:ring-focus-ring-secondary\
          active:border-border-bold active:bg-subtle\
          disabled:border-border-bold disabled:bg-base/50",
        ghost:
          "text-muted-foreground border border-transparent\
          hover:border-border \
          focus-visible:border-border-bold focus-visible:bg-faint focus-visible:ring-focus-ring-secondary \
          active:border-subtle active:bg-subtle",
        link: "text-primary underline-offset-4 hover:underline",
        tertiary:
          "bg-subtle text-foreground border border-border\
          hover:border-border-bold hover:bg-calm\
          focus-visible:border-border-bold focus-visible:bg-subtle focus-visible:ring-focus-ring-secondary\
          active:border-border active:bg-calm\
          disabled:border-border disabled:bg-subtle/50 [&_svg]:text-muted-foreground",
      },
      size: {
        xs: "h-6 px-1.5 py-1 gap-1 text-xs",
        default: "h-9 px-4 py-2",
        sm: "h-8 gap-2 px-3",
        lg: "h-10 px-6",
        icon: "size-8",
        iconSm: "size-6",
        iconXs: "size-5",
      },
      // Re-tokenized for dark surfaces (e.g. the bulk action bar) via the
      // compound variants below. Primary needs no compound — the brand green
      // is identical on dark per the UI kit.
      inverted: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "ghost",
        inverted: true,
        class:
          "text-inverted-foreground [&_svg]:text-inverted-foreground \
          hover:border-border-inverted \
          focus-visible:border-border-bold-inverted focus-visible:bg-faint-inverted focus-visible:ring-focus-ring-secondary-inverted \
          active:border-subtle-inverted active:bg-subtle-inverted",
      },
      {
        variant: "secondary",
        inverted: true,
        class:
          "bg-background-inverted text-inverted-foreground border-border-inverted [&_svg]:text-inverted-foreground \
          hover:border-border-bold-inverted hover:bg-faint-inverted \
          focus-visible:border-border-bold-inverted focus-visible:bg-subtle-inverted focus-visible:ring-focus-ring-secondary-inverted \
          active:border-border-bold-inverted active:bg-subtle-inverted \
          disabled:border-border-bold-inverted disabled:bg-background-inverted/50",
      },
      {
        variant: "tertiary",
        inverted: true,
        class:
          "bg-subtle-inverted text-inverted-foreground border-border-inverted [&_svg]:text-inverted-foreground \
          hover:border-border-bold-inverted hover:bg-calm-inverted \
          focus-visible:border-border-bold-inverted focus-visible:bg-subtle-inverted focus-visible:ring-focus-ring-secondary-inverted \
          active:border-border-inverted active:bg-calm-inverted \
          disabled:border-border-inverted disabled:bg-subtle-inverted/50",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      inverted: false,
    },
  },
);

function Button({
  className,
  variant,
  size,
  inverted,
  asChild = false,
  isLoading = false,
  loadingText,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    loadingText?: string;
  }) {
  const Comp = asChild ? Slot : "button";
  const spinnerSize = size === "xs" ? 12 : 16;

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, inverted, className }),
        isLoading && size === "xs" && "gap-2",
      )}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && !asChild && <Spinner size={spinnerSize} />}
      {isLoading && loadingText ? loadingText : children}
    </Comp>
  );
}

export { Button, buttonVariants };
